/**
 * "Direcciones de conversación" (concepto Chispa) — catálogo de presentación del
 * menú de modo libre. Los IDs/contrato con el backend viven en
 * `@orborix/shared-types` (`CONVERSATION_SEEDS`); acá vive sólo el chrome de UI:
 * color, glifo, copy. El texto del FOCO que ve el coach es backend-only.
 *
 * Cada dirección es una PERSONALIDAD (color + glifo + copy propios), no una fila
 * más. "Una situación" (roleplay) y "Noticias" NO son seeds: la primera abre el
 * hub de escenarios; la segunda (news-service Fase 5) elige una noticia real de
 * la DB y la inyecta en el prompt (contexto dinámico, como un scenario).
 */

import type { ComponentType } from 'react';
import type { ConversationSeed } from '@orborix/shared-types';

export type DirectionKind = 'seed' | 'scenario' | 'news' | 'soon';

export interface Direction {
  /** ID de UI (no necesariamente == seed). */
  id: string;
  /** Seed del backend (null en scenario/noticias). */
  seed: ConversationSeed | null;
  kind: DirectionKind;
  label: string;
  /** Etiqueta corta para el estado "dirección activa" (chip del trigger). */
  chip: string;
  /** Subtítulo de la card. */
  sub: string;
  /**
   * Color identitario (hex) — chip del trigger y acentos FUERA del menú.
   * Las cards del menú no lo usan: pintan duotono por categoría
   * (lima = Juegos, violeta = Temas).
   */
  color: string;
  Glyph: ComponentType<{ size?: number }>;
  /**
   * Juego de práctica (sección "Juegos" del menú, `GAME_SEEDS` del contrato):
   * mismo canal `{ seed }`, pero el coach lo abre como juego explícito.
   */
  isGame?: boolean;
}

// ─── Glifos por dirección (line-icons; heredan color del tile) ──────────────
const GIdeasGrandes = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3.4" fill="currentColor" stroke="none" />
    <ellipse cx="12" cy="12" rx="10" ry="4.4" transform="rotate(-28 12 12)" />
    <circle cx="20" cy="6.6" r="1.3" fill="currentColor" stroke="none" />
  </svg>
);
const GDebate = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 4 h 9 a 2 2 0 0 1 2 2 v 3 a 2 2 0 0 1 -2 2 H 8 l -3 2.4 V 11 H 3 Z" />
    <path d="M21 10 h -6 a 2 2 0 0 0 -2 2 v 3 a 2 2 0 0 0 2 2 h 1 v 2.4 L 19 19 h 2 V 10 Z" />
  </svg>
);
const GContame = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3 v 3 M12 18 v 3 M3 12 h 3 M18 12 h 3 M5.6 5.6 l 2.1 2.1 M16.3 16.3 l 2.1 2.1 M5.6 18.4 l 2.1 -2.1 M16.3 7.7 l 2.1 -2.1" />
    <circle cx="12" cy="12" r="3.1" fill="currentColor" stroke="none" />
  </svg>
);
const GStory = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 6 C 10 4.4, 6.5 4.4, 4 5 V 18.2 C 6.5 17.6, 10 17.6, 12 19" />
    <path d="M12 6 C 14 4.4, 17.5 4.4, 20 5 V 18.2 C 17.5 17.6, 14 17.6, 12 19" />
    <path d="M12 6 V 19" />
  </svg>
);
const GIdiom = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" stroke="none" aria-hidden="true">
    <path d="M5 6 C 2.6 7.4, 2 10, 2.4 12.6 C 2.7 14.3, 4 15.2, 5.4 14.9 C 6.7 14.6, 7.5 13.4, 7.2 12 C 6.9 10.7, 5.7 10, 4.6 10.2 C 4.9 8.9, 5.8 8, 6.8 7.4 Z" />
    <path d="M15 6 C 12.6 7.4, 12 10, 12.4 12.6 C 12.7 14.3, 14 15.2, 15.4 14.9 C 16.7 14.6, 17.5 13.4, 17.2 12 C 16.9 10.7, 15.7 10, 14.6 10.2 C 14.9 8.9, 15.8 8, 16.8 7.4 Z" />
  </svg>
);
const GYours = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20 C 12 20, 3.5 14.5, 3.5 8.8 A 4.3 4.3 0 0 1 12 7.2 A 4.3 4.3 0 0 1 20.5 8.8 C 20.5 14.5, 12 20, 12 20 Z" />
  </svg>
);
const GScenario = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.5 6 h 11 a 1.6 1.6 0 0 1 1.6 1.6 v 4.2 a 1.6 1.6 0 0 1 -1.6 1.6 H 8 l -3.4 2.6 V 13.4 H 3.5 Z" />
    <path d="M11 13.6 a 1.6 1.6 0 0 0 1.5 1 H 18 l 2.5 2 V 12 a 1.6 1.6 0 0 0 -1.6 -1.6 h -1.3" opacity="0.92" />
  </svg>
);
const GNews = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 5 h 12 v 14 H 6 a 2 2 0 0 1 -2 -2 Z" />
    <path d="M16 9 h 3 v 8 a 2 2 0 0 1 -2 2" />
    <path d="M7 8.5 h 6 M7 12 h 6 M7 15.5 h 4" />
  </svg>
);

// ─── Glifos de los juegos ────────────────────────────────────────────────────
const GTranslateIt = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8.5 h 13.5 M17.5 8.5 l -3.4 -3.4 M20 15.5 h -13.5 M6.5 15.5 l 3.4 3.4" />
  </svg>
);
const GGuessIt = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8.6 9 a 3.4 3.4 0 1 1 5 3 c -1.1 .65 -1.6 1.3 -1.6 2.4" />
    <circle cx="12" cy="18.2" r="1.25" fill="currentColor" stroke="none" />
  </svg>
);
const GSecretWord = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12 C 5.6 7.6, 18.4 7.6, 21 12 C 18.4 16.4, 5.6 16.4, 3 12 Z" />
    <circle cx="12" cy="12" r="2.3" fill="currentColor" stroke="none" />
    <path d="M5 19.5 L 19 4.5" />
  </svg>
);
const GThreeWords = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4.5" width="8.4" height="5.4" rx="1.6" />
    <rect x="13.4" y="7.5" width="7.6" height="5.4" rx="1.6" />
    <rect x="6.5" y="14.5" width="8.8" height="5.4" rx="1.6" />
  </svg>
);
const GTimeMachine = ({ size = 22 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="13" r="7" />
    <path d="M12 9.6 V 13 l 2.6 1.8" />
    <path d="M19.5 3.2 l 2 .9 -.9 2" />
    <path d="M21.3 4 A 9.4 9.4 0 0 0 13.5 1.9" opacity="0.9" />
  </svg>
);

/** Chispa: glifo del trigger + acento del header/feedback. */
export function DirSpark({ size = 16 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M12 2 C 12.5 6.5, 13.6 8.4, 18.5 9.2 C 13.6 10, 12.5 11.9, 12 16.4 C 11.5 11.9, 10.4 10, 5.5 9.2 C 10.4 8.4, 11.5 6.5, 12 2 Z" transform="translate(0 1.5)" />
      <path d="M19 15 C 19.2 16.6, 19.6 17.2, 21 17.5 C 19.6 17.8, 19.2 18.4, 19 20 C 18.8 18.4, 18.4 17.8, 17 17.5 C 18.4 17.2, 18.8 16.6, 19 15 Z" />
    </svg>
  );
}

/** Orden = el del menú. */
export const DIRECTIONS: readonly Direction[] = [
  { id: 'news', seed: null, kind: 'news', label: 'Noticias', chip: 'Noticias', sub: 'Elegimos una noticia reciente al azar', color: '#ffb454', Glyph: GNews },
  { id: 'bigideas', seed: 'big-ideas', kind: 'seed', label: 'Ideas grandes', chip: 'Ideas grandes', sub: 'Filosofemos un rato', color: '#a78bfa', Glyph: GIdeasGrandes },
  { id: 'debate', seed: 'debate', kind: 'seed', label: 'Debate', chip: 'Debate', sub: 'Te llevo la contra', color: '#ff9d6c', Glyph: GDebate },
  { id: 'curio', seed: 'tell-me', kind: 'seed', label: 'Contame algo', chip: 'Contame algo', sub: 'Un dato que no sabías', color: '#5ce1e6', Glyph: GContame },
  { id: 'story', seed: 'storytelling', kind: 'seed', label: 'Storytelling', chip: 'Storytelling', sub: 'Una historia, vos o yo', color: '#ff6ec7', Glyph: GStory },
  { id: 'idiom', seed: 'expression', kind: 'seed', label: 'Expresión del día', chip: 'Expresión', sub: 'Un modismo, en contexto', color: '#d4f23e', Glyph: GIdiom },
  { id: 'yours', seed: 'yours', kind: 'seed', label: 'Lo tuyo', chip: 'Lo tuyo', sub: 'De lo que te gusta', color: '#7cc4ff', Glyph: GYours },
  // Juegos: los GAME_SEEDS + el roleplay ("Una situación", kind scenario — no
  // es seed pero vive en esta sección). El menú los muestra PRIMERO.
  { id: 'translateit', seed: 'translate-it', kind: 'seed', isGame: true, label: 'Decilo en inglés', chip: 'Decilo en inglés', sub: 'Yo en español, vos en inglés', color: '#ff8787', Glyph: GTranslateIt },
  { id: 'scenario', seed: null, kind: 'scenario', isGame: true, label: 'Una situación', chip: 'Situación', sub: 'Un roleplay real', color: '#6df0a8', Glyph: GScenario },
  { id: 'guessit', seed: 'guess-it', kind: 'seed', isGame: true, label: 'Adivinalo', chip: 'Adivinalo', sub: 'Pienso algo, vos preguntás', color: '#ffd43b', Glyph: GGuessIt },
  { id: 'secretword', seed: 'describe-it', kind: 'seed', isGame: true, label: 'Palabra secreta', chip: 'Palabra secreta', sub: 'Describila y la adivino en serio', color: '#f783ac', Glyph: GSecretWord },
  { id: 'threewords', seed: 'three-words', kind: 'seed', isGame: true, label: 'Tres palabras', chip: 'Tres palabras', sub: 'Una mini-historia con las tres', color: '#69db7c', Glyph: GThreeWords },
  { id: 'timemachine', seed: 'time-machine', kind: 'seed', isGame: true, label: 'Máquina del tiempo', chip: 'Máq. del tiempo', sub: 'La misma frase, en otro tiempo', color: '#9775fa', Glyph: GTimeMachine },
];

/** Dirección activa a partir del seed del backend (null = charla libre). */
export function directionForSeed(seed: ConversationSeed | null): Direction | null {
  if (!seed) return null;
  return DIRECTIONS.find((d) => d.seed === seed) ?? null;
}
